import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Snackbar,
  Alert,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { collection, query, where, or, getDocs, doc, deleteDoc, getDoc, updateDoc, onSnapshot, runTransaction } from 'firebase/firestore';
import { db, auth } from '../firebaseConfig'; // Adjust this import path as needed
import BookGuest from './BookGuest';
import WaitlistModal from './WaitlistModal';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import axios from 'axios';
import { registerForPush, isUninstalledIos } from '../pushNotifications';

const toFamilyEntry = (d) => {
  const data = d.data();
  return {
    docId: d.id,
    placeIndex: data.placeIndex ?? null,
    createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : null
  };
};


const WeeklyBookings = ({defaultDay, defaultTime, defaultWeek}) => {
  const adminPhoneNumbers = [
   // "+447725868605",
   // "+447725868606",
    "+447989952253",
    "+447740196104"
  ]

  const [refreshing, setRefreshing] = useState(false);
  const refreshTimeoutRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [selectedDay, setSelectedDay] = useState(0);

  // The household's waitlist entries for the currently selected slot, and the
  // children that decide how many places it may hold.
  const [familyWaitlist, setFamilyWaitlist] = useState([]);
  const [myChildren, setMyChildren] = useState([]);
  const [childrenLoaded, setChildrenLoaded] = useState(false);
  // Which waitlist action is in flight - 'join', 'leave', or null. Naming the
  // action lets only the control that was pressed show a spinner.
  const [waitlistAction, setWaitlistAction] = useState(null);
  const [checkingNotification, setCheckingNotification] = useState(true);

  //if day is set (e.g. Mon, Tues etc), set selectedDay to that day as the index:
  
  const [timeOfDay, setTimeOfDay] = useState('morning');
  const [loading, setLoading] = useState(true);
  const [weekOptions, setWeekOptions] = useState([]);
  const [selectedWeekString, setSelectedWeekString] = useState('');
  const [isBookGuestOpen, setIsBookGuestOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  // Set when another household is first in the queue for the seat being booked
  const [queueWarning, setQueueWarning] = useState(null);
  const [checkingQueue, setCheckingQueue] = useState(false);
  // Member id whose released seat is being reclaimed
  const [reclaimingId, setReclaimingId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [spotToFreeUp, setSpotToFreeUp] = useState(null);

  const [bookings, setBookings] = useState({
    members: {
      0: { morning: [], afternoon: [] },
      1: { morning: [], afternoon: [] },
      2: { morning: [], afternoon: [] },
      3: { morning: [], afternoon: [] },
      4: { morning: [], afternoon: [] },
    },
    guests: {
      0: { morning: [], afternoon: [] },
      1: { morning: [], afternoon: [] },
      2: { morning: [], afternoon: [] },
      3: { morning: [], afternoon: [] },
      4: { morning: [], afternoon: [] },
    }
  });

  const MAX_SLOTS = 53; // Maximum number of slots per session

  const [freeUpModalOpen, setFreeUpModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [guestToDelete, setGuestToDelete] = useState(null);

  const [isWaitlistModalOpen, setIsWaitlistModalOpen] = useState(false);
  const handleWaitlistClick = () => {
    setIsWaitlistModalOpen(true);
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      console.log("user", user);
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  // Load the household's active children - they decide how many waitlist places
  // the household may hold (one per child).
  useEffect(() => {
    let ignore = false;

    const loadChildren = async () => {
      if (!currentUser) {
        setMyChildren([]);
        setChildrenLoaded(true);
        return;
      }

      setChildrenLoaded(false);
      try {
        const membersQuery = query(collection(db, 'members'), or(
          where('userId', '==', currentUser.uid),
          where('additionalUserId', '==', currentUser.uid)
        ));
        const querySnapshot = await getDocs(membersQuery);
        const kids = querySnapshot.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(member => member.status === 'Active');
        if (!ignore) setMyChildren(kids);
      } catch (error) {
        console.error('Error loading children:', error);
        if (!ignore) setMyChildren([]);
      } finally {
        if (!ignore) setChildrenLoaded(true);
      }
    };

    loadChildren();
    return () => { ignore = true; };
  }, [currentUser]);

  //if defaultDay, defaultTime, defaultWeek update the default values:
    useEffect(() => {
    if (defaultDay !== null) {
        setSelectedDay(defaultDay);
    }
    if (defaultTime !== null) {
        setTimeOfDay(defaultTime);
    }
    if (defaultWeek) {
        setSelectedWeekString(defaultWeek);
    }
    }, [defaultDay, defaultTime, defaultWeek]);


    // Listen rather than fetch, so both the slot's count and the household's
    // own places move when anyone - the other parent included - joins or leaves.
    useEffect(() => {
      setFamilyWaitlist([]);

      if (!selectedWeekString || selectedDay === null || !timeOfDay) {
        setCheckingNotification(false);
        return;
      }

      setCheckingNotification(true);
      const q = query(
        collection(db, 'waitlist'),
        where('weekStart', '==', selectedWeekString),
        where('day', '==', selectedDay),
        where('timeOfDay', '==', timeOfDay)
      );

      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        setWaitlistCount(querySnapshot.size);
        // An entry is the household's if the user is either parent on it
        setFamilyWaitlist(currentUser
          ? querySnapshot.docs
            .filter(d => d.data().userId === currentUser.uid || d.data().additionalUserId === currentUser.uid)
            .map(toFamilyEntry)
          : []);
        setCheckingNotification(false);
      }, (error) => {
        console.error('Error checking waitlist:', error);
        setWaitlistCount(0);
        setCheckingNotification(false);
      });

      return () => unsubscribe();
    }, [currentUser, selectedWeekString, selectedDay, timeOfDay]);

  const getWeekStart = (date) => {
    const d = new Date(date);
    d.setDate(d.getDate() - d.getDay() + 1); // Set to Monday of the week
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  const formatDateStringSunday = (date) => {
    
    //ensure date is the last sunday:
    date.setDate(date.getDate() - 1);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');



    console.log(`${year}-${month}-${day}`);
    return `${year}-${month}-${day}`;
  };

  const formatDateForDisplay = (date) => {
    const startDate = new Date(date);
    //add one day to startDate:
    startDate.setDate(startDate.getDate() + 1);
    const endDate = new Date(startDate);

    endDate.setDate(endDate.getDate() + 4);
  
    const startMonth = startDate.toLocaleDateString('en-US', { month: 'short' });
    const endMonth = endDate.toLocaleDateString('en-US', { month: 'short' });
    const startDay = startDate.getDate();
    const endDay = endDate.getDate();
  
    if (startMonth === endMonth) {
      return `${startMonth} ${startDay}-${endDay}`;
    } else {
      return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
    }
  };

  // The household at the front of the queue for a slot, or null if nobody is
  // waiting. Entries without a createdAt keep their query order.
  const getFirstInLine = async (day, timeOfDay, weekStart) => {
    const waitlistSnapshot = await getDocs(query(
      collection(db, 'waitlist'),
      where('day', '==', day),
      where('timeOfDay', '==', timeOfDay),
      where('weekStart', '==', weekStart)
    ));

    if (waitlistSnapshot.empty) return null;

    const waitlistEntries = waitlistSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate()
    }));

    return waitlistEntries.sort((a, b) =>
      (a.createdAt && b.createdAt) ? a.createdAt - b.createdAt : 0
    )[0];
  };

  const resolveHouseholdName = async (entry) => {
    for (const uid of [entry.userId, entry.additionalUserId].filter(Boolean)) {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists() && userDoc.data().userName) return userDoc.data().userName;
    }
    return 'Someone else';
  };

  const openBookGuest = (schedule) => {
    setSelectedSchedule(schedule);
    setIsBookGuestOpen(true);
  };

  // Anyone may take an available seat, but if another household is first in
  // the queue for it, the booker has to confirm they mean to go ahead of them.
  // Returns that household's name, or null when nobody else is ahead.
  const getOtherHouseholdFirstInLine = async (day, timeOfDay) => {
    try {
      const firstInLine = await getFirstInLine(day, timeOfDay, selectedWeekString);
      const isOwnHousehold = firstInLine && currentUser &&
        (firstInLine.userId === currentUser.uid || firstInLine.additionalUserId === currentUser.uid);

      return (firstInLine && !isOwnHousehold) ? await resolveHouseholdName(firstInLine) : null;
    } catch (error) {
      // The warning is advisory - a failed lookup shouldn't stop the booking
      console.error('Error checking the waitlist:', error);
      return null;
    }
  };

  const handleOpenBookGuest = async (schedule) => {
    if (checkingQueue) return;

    setCheckingQueue(true);
    const queuedName = await getOtherHouseholdFirstInLine(schedule.day, schedule.timeOfDay);
    setCheckingQueue(false);

    if (queuedName) {
      setQueueWarning({ name: queuedName, proceed: () => openBookGuest(schedule) });
      return;
    }

    openBookGuest(schedule);
  };

  const handleQueueWarningYes = () => {
    const { proceed } = queueWarning;
    setQueueWarning(null);
    proceed();
  };

  const handleQueueWarningNo = () => {
    setQueueWarning(null);
  };

  const handleManualRefresh = () => {
    if (!refreshing) {
      setRefreshing(true);
      refreshWeekOptions();
      fetchBookings(selectedWeekString);
    }
  };

  const handleCloseBookGuest = () => {
    setIsBookGuestOpen(false);
    setSelectedSchedule(null);
    fetchBookings(selectedWeekString);
  };

  useEffect(() => {
    refreshWeekOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Every waitlist entry the household holds for the selected slot. A doc carries
  // the primary parent's uid, so the second parent only finds it via
  // additionalUserId - both queries are needed.
  const readFamilyWaitlist = useCallback(async () => {
    if (!currentUser || !selectedWeekString || selectedDay === null || !timeOfDay) return [];

    const notificationsRef = collection(db, 'waitlist');
    const slot = [
      where('weekStart', '==', selectedWeekString),
      where('day', '==', selectedDay),
      where('timeOfDay', '==', timeOfDay)
    ];
    const [snap1, snap2] = await Promise.all([
      getDocs(query(notificationsRef, where('userId', '==', currentUser.uid), ...slot)),
      getDocs(query(notificationsRef, where('additionalUserId', '==', currentUser.uid), ...slot))
    ]);

    // Dedupe by doc id - a doc where the user is both parents matches twice.
    const byId = new Map();
    [...snap1.docs, ...snap2.docs].forEach((d) => {
      byId.set(d.id, toFamilyEntry(d));
    });
    return [...byId.values()];
  }, [currentUser, selectedWeekString, selectedDay, timeOfDay]);


  //write a function that updates the weekOptions when the week changes (called on refresh):
  const refreshWeekOptions = () => {
    const today = new Date();
    if (selectedWeekString){
      //if selectedWeekString is in the start of last week, refresh:
      if (getWeekStart(new Date(selectedWeekString)) < getWeekStart(today)){
        //week is in the past, continue - refresh the list...
      }
      else{
        return;
      }
    }

    const thisWeekStart = getWeekStart(today);
    const nextWeekStart = new Date(thisWeekStart);
    nextWeekStart.setDate(nextWeekStart.getDate() + 7);

    const thisWeekString = formatDateStringSunday(thisWeekStart);
    const nextWeekString = formatDateStringSunday(nextWeekStart);

    setWeekOptions([
      { label: `This week (${formatDateForDisplay(thisWeekStart)})`, value: thisWeekString },
      { label: `Next week (${formatDateForDisplay(nextWeekStart)})`, value: nextWeekString }
    ]);
    
    setSelectedWeekString(thisWeekString);

    fetchBookings(thisWeekString)
  }

  const fetchBookings = useCallback((weekStart) => {
    setLoading(true);
    setRefreshing(true);
  
    let newBookings = {
      members: {},
      guests: {},
      notAttending: {}
    };
  
    // Initialize the structure for each day and time
    for (let i = 0; i < 5; i++) {
      newBookings.members[i] = { morning: [], afternoon: [] };
      newBookings.guests[i] = { morning: [], afternoon: [] };
      newBookings.notAttending[i] = { morning: [], afternoon: [] };
    }
  
    // Fetch all active members
    const membersQuery = query(collection(db, 'members'), where('status', '==', 'Active'));
    const unsubscribeMembers = onSnapshot(membersQuery, (membersSnapshot) => {

      setLoading(true);
      // Reset members and notAttending data for each snapshot
      for (let i = 0; i < 5; i++) {
        newBookings.members[i] = { morning: [], afternoon: [] };
        newBookings.notAttending[i] = { morning: [], afternoon: [] };
      }
  
      membersSnapshot.forEach((doc) => {
        const member = doc.data();
        const customSchedule = member.customSchedules?.find(
          schedule => schedule.weekStart === weekStart
        );
        const scheduleToUse = customSchedule ? customSchedule.schedule : (member.defaultSchedule || {});
  
        Object.keys(scheduleToUse).forEach((day) => {
          ['morning', 'afternoon'].forEach((timeOfDay) => {
            if (scheduleToUse[day][timeOfDay]) {
              newBookings.members[day][timeOfDay].push({
                id: doc.id,
                userId: member.userId,
                additionalUserId: member.additionalUserId,
                name: member.childName
              });
            } else if (member.defaultSchedule[day][timeOfDay] && !scheduleToUse[day][timeOfDay]) {
              newBookings.notAttending[day][timeOfDay].push({
                id: doc.id,
                userId: member.userId,
                additionalUserId: member.additionalUserId,
                name: member.childName
              });
            }
          });
        });
      });
  
      setBookings(prevBookings => ({
        ...prevBookings,
        members: newBookings.members,
        notAttending: newBookings.notAttending
      }));
      setLoading(false);
      setRefreshing(false);
    });
  
    // Fetch guests for the specific week
    const guestsQuery = query(
      collection(db, 'guests'),
      where('status', '==', 'Active'),
      where('schedule.weekStart', '==', weekStart)
    );
    const unsubscribeGuests = onSnapshot(guestsQuery, (guestsSnapshot) => {
      setLoading(true);
      // Reset guests data for each snapshot
      for (let i = 0; i < 5; i++) {
        newBookings.guests[i] = { morning: [], afternoon: [] };
      }
  
      guestsSnapshot.forEach((doc) => {
        const guest = doc.data();
        const { schedule } = guest;
        const day = schedule.day;
        const timeOfDay = schedule.timeOfDay;
  
        if (newBookings.guests[day] && newBookings.guests[day][timeOfDay]) {
          newBookings.guests[day][timeOfDay].push({
            id: doc.id,
            name: guest.guestName,
            userId: guest.userId,
            bookingNote: guest.bookingNote
          });
        }
      });
  
      setBookings(prevBookings => ({
        ...prevBookings,
        guests: newBookings.guests
      }));
      setLoading(false);
      setRefreshing(false);
    });
  
    return () => {
      unsubscribeMembers();
      unsubscribeGuests();
    };
  }, []);

  useEffect(() => {
    let unsubscribe;
    if (selectedWeekString) {
      unsubscribe = fetchBookings(selectedWeekString);
    }
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
   
  }, [selectedWeekString, fetchBookings]);

  useEffect(() => {
    const startAutoRefresh = () => {
      refreshTimeoutRef.current = setTimeout(() => {
        refreshWeekOptions();
        //fetchBookings(selectedWeekString);
        startAutoRefresh();
      }, 60000);
    };

    startAutoRefresh();

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchBookings, selectedWeekString]);

  /*useEffect(() => {
    const handleScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        if (scrollTop + clientHeight >= scrollHeight - 50 && !refreshing) {
          handleRefresh();
        }
      }
    };

    const currentScrollContainer = scrollContainerRef.current;
    if (currentScrollContainer) {
      currentScrollContainer.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (currentScrollContainer) {
        currentScrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [refreshing]);

  const handleRefresh = () => {
    if (!refreshing) {
      setRefreshing(true);
      refreshWeekOptions();
      fetchBookings(selectedWeekString);
    }
  };*/

  const handleDayChange = (event, newValue) => {
    setSelectedDay(newValue);
  };

  const handleTimeChange = (event, newTime) => {
    if (newTime !== null) {
      setTimeOfDay(newTime);
    }
  };

  const handleWeekChange = (event) => {
    setSelectedWeekString(event.target.value);
  };


  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

  const getNotAttendingForSelectedDayAndTime = () => {
    return bookings.notAttending[selectedDay]?.[timeOfDay] || [];
  };

  const getBookingsForSelectedDayAndTime = () => {
    const bookingsForDay = bookings.members[selectedDay]?.[timeOfDay] || [];
    
    // Sort the bookings
    return bookingsForDay.sort((a, b) => {
      // Check if a or b is owned by or assigned to current user
      const aIsCurrentUser = a.userId === currentUser?.uid || a.additionalUserId === currentUser?.uid;
      const bIsCurrentUser = b.userId === currentUser?.uid || b.additionalUserId === currentUser?.uid;
      
      // Current user's bookings come first
      if (aIsCurrentUser && !bIsCurrentUser) return -1;
      if (!aIsCurrentUser && bIsCurrentUser) return 1;
      
      // Then sort alphabetically by name
      return a.name.localeCompare(b.name);
    });
  };

  const getGuestsForSelectedDayAndTime = () => {
    const guestsForDay = bookings.guests[selectedDay]?.[timeOfDay] || [];
    
    // Sort the guests
    return guestsForDay.sort((a, b) => {
      // Check if a or b is owned by or assigned to current user
      const aIsCurrentUser = a.userId === currentUser?.uid || a.additionalUserId === currentUser?.uid;
      const bIsCurrentUser = b.userId === currentUser?.uid || b.additionalUserId === currentUser?.uid;
      
      // Current user's guests come first
      if (aIsCurrentUser && !bIsCurrentUser) return -1;
      if (!aIsCurrentUser && bIsCurrentUser) return 1;
      
      // Then sort alphabetically by name
      return a.name.localeCompare(b.name);
    });
  };

  // A household that has given up every one of its children's seats for this
  // session can't then book a guest into it.
  const hasReleasedAllChildSeats = () => {
    if (!currentUser) return false;
    const isOwnChild = (person) =>
      person.userId === currentUser.uid || person.additionalUserId === currentUser.uid;

    const released = getNotAttendingForSelectedDayAndTime().filter(isOwnChild).length;
    const attending = getBookingsForSelectedDayAndTime().filter(isOwnChild).length;
    return released > 0 && attending === 0;
  };

  const getFreeSpots = () => {
    const bookedSpots = getBookingsForSelectedDayAndTime().length + getGuestsForSelectedDayAndTime().length;
    const freeSpots = MAX_SLOTS - bookedSpots;
    return freeSpots;
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // One waitlist place per child, minimum one - a parent with no linked children
  // keeps the single place they have today.
  const maxPlaces = Math.max(myChildren.length, 1);
  const placesHeld = familyWaitlist.length;
  // Don't let anyone join before we know how many places they're entitled to
  const waitlistDisabled = checkingNotification || !childrenLoaded || waitlistAction !== null;

  // Both parents must write the same pair, so either of them finds every entry
  // the household holds. The acting user is always one of the two.
  const getHousehold = () => {
    const ownChild = myChildren.find(child => child.userId === currentUser?.uid);
    if (ownChild) {
      const partnered = myChildren.find(
        child => child.userId === currentUser?.uid && child.additionalUserId
      );
      return {
        userId: currentUser?.uid,
        additionalUserId: partnered ? partnered.additionalUserId : null
      };
    }

    // The acting user is the second parent on at least one child
    const sharedChild = myChildren.find(child => child.additionalUserId === currentUser?.uid);
    if (sharedChild) {
      return { userId: sharedChild.userId, additionalUserId: currentUser?.uid };
    }

    return { userId: currentUser?.uid, additionalUserId: null };
  };

  // A guest seat isn't tied to a particular child, so booking one gives up a
  // single place. Always release the newest, which keeps the household's
  // earliest position in the queue.
  const removeOneFamilyWaitlistEntry = async () => {
    try {
      const entries = await readFamilyWaitlist();
      if (entries.length === 0) return null;

      const newest = [...entries].sort((a, b) =>
        (a.createdAt && b.createdAt) ? b.createdAt - a.createdAt : 0
      )[0];

      await deleteDoc(doc(db, 'waitlist', newest.docId));
      setFamilyWaitlist(await readFamilyWaitlist());
      return newest;
    } catch (error) {
      console.error('Error removing from waitlist:', error);
      return null;
    }
  };

  const onBookingMade = async () => {
    // Give up one of the household's waitlist places if it holds any
    let removed = null;
    if (currentUser) {
      removed = await removeOneFamilyWaitlistEntry();
    }

    const placesLeft = Math.max(placesHeld - 1, 0);
    setSnackbar({
      open: true,
      message: (removed && maxPlaces > 1)
        ? `Booking successful! You now hold ${placesLeft} of ${maxPlaces} waitlist places for this slot.`
        : 'Booking successful!',
      severity: 'success'
    });
    fetchBookings(selectedWeekString);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleFreeUpSlotClick = (day, time, person) => {
    //display confirmation modal to free up slot:

    setSpotToFreeUp({ day: day, timeOfDay: time, memberId: person.id, person: person });
    setFreeUpModalOpen(true);
    console.log(`Open slot clicked for ${person.id} on ${day} ${time}`);
  };


  // Marks a member as attending or not for one slot of the selected week, via
  // that week's custom schedule (created from their default if there isn't one).
  const setMemberAttendance = async (memberId, day, time, attending) => {
    const memberRef = doc(db, 'members', memberId);
    const memberDoc = await getDoc(memberRef);

    if (!memberDoc.exists()) {
      throw new Error('Member document not found');
    }

    const memberData = memberDoc.data();
    const customSchedules = memberData.customSchedules || [];

    // Find existing custom schedule for the selected week
    const existingCustomScheduleIndex = customSchedules.findIndex(
      schedule => schedule.weekStart === selectedWeekString
    );

    let updatedCustomSchedules;

    if (existingCustomScheduleIndex !== -1) {
      // Merge with existing custom schedule
      updatedCustomSchedules = [...customSchedules];
      updatedCustomSchedules[existingCustomScheduleIndex] = {
        ...updatedCustomSchedules[existingCustomScheduleIndex],
        schedule: {
          ...updatedCustomSchedules[existingCustomScheduleIndex].schedule,
          [day]: {
            ...updatedCustomSchedules[existingCustomScheduleIndex].schedule[day],
            [time]: attending
          }
        }
      };
    } else {
      // Create new custom schedule
      const newCustomSchedule = {
        weekStart: selectedWeekString,
        schedule: {
          ...memberData.defaultSchedule,
          [day]: {
            ...memberData.defaultSchedule[day],
            [time]: attending
          }
        }
      };
      updatedCustomSchedules = [...customSchedules, newCustomSchedule];
    }

    // Update the member's custom schedules
    await updateDoc(memberRef, {
      customSchedules: updatedCustomSchedules,
      updatedDate: new Date()
    });
  };

  const reclaimSeat = async (person, day, time) => {
    setReclaimingId(person.id);
    try {
      await setMemberAttendance(person.id, day, time, true);

      // The child has their seat back, so like a guest booking this gives up
      // one of the household's waitlist places for the slot, if it holds any.
      const removed = await removeOneFamilyWaitlistEntry();
      setSnackbar({
        open: true,
        message: removed
          ? `Seat reclaimed for ${person.name}. You've given up one waitlist place for this slot.`
          : `Seat reclaimed for ${person.name}.`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error reclaiming seat:', error);
      setSnackbar({
        open: true,
        message: 'Failed to reclaim seat. Please try again.',
        severity: 'error'
      });
    } finally {
      setReclaimingId(null);
    }
  };

  const handleReclaimSeatClick = async (person) => {
    if (reclaimingId) return;

    const day = selectedDay;
    const time = timeOfDay;

    setReclaimingId(person.id);
    const queuedName = await getOtherHouseholdFirstInLine(day, time);

    if (queuedName) {
      setReclaimingId(null);
      setQueueWarning({ name: queuedName, proceed: () => reclaimSeat(person, day, time) });
      return;
    }

    await reclaimSeat(person, day, time);
  };

  const handleFreeUpConfirm = async () => {
    if (!spotToFreeUp) return;

    // Close straight away - notifying the waitlist can take a few seconds, and
    // the outcome is reported in the snackbar either way.
    const spot = spotToFreeUp;
    setFreeUpModalOpen(false);
    setSpotToFreeUp(null);

    try {
      await setMemberAttendance(spot.memberId, spot.day, spot.timeOfDay, false);

       // Call notifyWaitlist function
      await notifyWaitlist(spot.day, spot.timeOfDay, selectedWeekString);
    } catch (error) {
      console.error("Error freeing up spot:", error);
      setSnackbar({
        open: true,
        message: 'Failed to free up spot. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleFreeUpCancel = () => {
    setFreeUpModalOpen(false);
    setSpotToFreeUp(null);
  };

  const formatDate = (weekStart, dayIndex) => {
    const date = new Date(weekStart);
    date.setDate(date.getDate() + dayIndex + 1);
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  };

  const formatDateShort = (weekStart, dayIndex) => {
    const date = new Date(weekStart);
    date.setDate(date.getDate() + dayIndex + 1);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };  

  // Entries carry the household's primary parent, so fall back to the second
  // parent when the primary has no number - one text per household, never both.
  const resolveHouseholdPhone = async (firstInLine) => {
    let userDoc = await getDoc(doc(db, 'users', firstInLine.userId));
    if ((!userDoc.exists() || !userDoc.data().userPhone) && firstInLine.additionalUserId) {
      const secondParentDoc = await getDoc(doc(db, 'users', firstInLine.additionalUserId));
      if (secondParentDoc.exists() && secondParentDoc.data().userPhone) {
        userDoc = secondParentDoc;
      }
    }

    return userDoc.exists() ? (userDoc.data().userPhone || null) : null;
  };

  const notifyWaitlist = async (day, timeOfDay, weekStart) => {
    try {
      const firstInLine = await getFirstInLine(day, timeOfDay, weekStart);

      if (!firstInLine) {
        console.log('No users on waitlist for this slot');
        return;
      }

      // Push reaches every device the household has registered, across both
      // parents. Costs nothing, so there's no reason to pick just one of them.
      const userIds = [firstInLine.userId, firstInLine.additionalUserId].filter(Boolean);

      try {
        const { data } = await axios.post('/api/send-push', {
          userIds,
          title: `A spot is open for the ${timeOfDay} of ${formatDate(weekStart, day)}!`,
          body: "You're next in line. Book now or leave the waitlist.",
          url: 'https://www.hertscoach.uk'
        });

        if (data.sent > 0) {
          setSnackbar({
            open: true,
            message: 'Notified the first person on the waitlist',
            severity: 'success'
          });
          return;
        }
      } catch (error) {
        console.error('Failed to send push notification:', error);
      }

      // No registered devices, or every send failed - fall back to SMS. iPhone
      // parents who haven't installed the app can't receive push at all.
      const phone = await resolveHouseholdPhone(firstInLine);

      if (phone) {
        try {
          await axios.post('/api/send-sms', {
            to: phone,
            message: `A spot is open for the ${timeOfDay} of ${formatDate(weekStart, day)}! You're next in line. Book now or leave the waitlist: https://hertscoach.uk`
          });

          setSnackbar({
            open: true,
            message: 'Notified the first person on the waitlist',
            severity: 'success'
          });
          return;
        } catch (error) {
          console.error(`Failed to send notification:`, error);
        }
      }

      // Delivery failed, but the release itself succeeded - don't blame the
      // parent who gave up the seat.
      setSnackbar({
        open: true,
        message: 'Your spot has been released',
        severity: 'success'
      });

    } catch (error) {
      console.error('Error in notifyWaitlist:', error);
      setSnackbar({
        open: true,
        message: 'Your spot has been released',
        severity: 'success'
      });
    }
  };

  // Take one of the household's waitlist places
  const handleJoinWaitlist = async () => {
    if (!currentUser) {
      setSnackbar({
        open: true,
        message: 'Please sign in to set notifications',
        severity: 'warning'
      });
      return;
    }

    if (waitlistAction || placesHeld >= maxPlaces) return;

    setWaitlistAction('join');
    try {
      const household = getHousehold();

      // Lowest place not already taken. Legacy entries carry no placeIndex, but
      // they still count towards placesHeld, so an index is always free here.
      const used = new Set(familyWaitlist.map(entry => entry.placeIndex).filter(i => i !== null));
      let placeIndex = 0;
      while (used.has(placeIndex)) placeIndex += 1;

      // A deterministic id makes the write idempotent, so a double-click or a
      // second tab can't quietly burn one of the household's places.
      const waitlistDocRef = doc(
        db,
        'waitlist',
        `${selectedWeekString}_${selectedDay}_${timeOfDay}_${household.userId}_${placeIndex}`
      );

      await runTransaction(db, async (transaction) => {
        const existing = await transaction.get(waitlistDocRef);
        if (existing.exists()) return; // already taken - keep its place in the queue
        transaction.set(waitlistDocRef, {
          userId: household.userId,
          ...(household.additionalUserId ? { additionalUserId: household.additionalUserId } : {}),
          placeIndex: placeIndex,
          weekStart: selectedWeekString,
          day: selectedDay,
          timeOfDay: timeOfDay,
          createdAt: new Date()
        });
      });

      const entries = await readFamilyWaitlist();
      setFamilyWaitlist(entries);

      // The place is committed and on screen, so free the controls here. What
      // follows can sit on a browser permission prompt until it's answered, and
      // leaving the waitlist must not wait on that.
      setWaitlistAction(null);

      // Highest-intent moment to ask, and the reason for the permission is
      // obvious. The place is already committed, so a refusal changes nothing
      // except how we say we'll reach them - SMS still covers everyone.
      const push = await registerForPush(currentUser.uid);
      const promise = push.ok
        ? "We'll notify you when a spot opens up."
        : (push.reason === 'unsupported' && isUninstalledIos())
          ? "We'll text you. Add the app to your home screen for push notifications."
          : "We'll text you when a spot opens up.";

      setSnackbar({
        open: true,
        message: maxPlaces > 1
          ? `${promise} You now hold ${entries.length} of ${maxPlaces} waitlist places for this slot.`
          : promise,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error toggling notification:', error);
      setSnackbar({
        open: true,
        message: 'Error setting notification. Please try again.',
        severity: 'error'
      });
    } finally {
      setWaitlistAction(null);
    }
  };

  // Give one of the household's waitlist places back
  const handleLeaveWaitlist = async () => {
    if (waitlistAction || placesHeld === 0) return;

    setWaitlistAction('leave');
    try {
      const removed = await removeOneFamilyWaitlistEntry();
      setSnackbar({
        open: true,
        message: (removed && maxPlaces > 1)
          ? `Removed from waitlist. You now hold ${Math.max(placesHeld - 1, 0)} of ${maxPlaces} waitlist places for this slot.`
          : 'Removed from waitlist',
        severity: 'info'
      });
    } finally {
      setWaitlistAction(null);
    }
  };


  const handleDeleteClick = (guest) => {
    setGuestToDelete(guest);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!guestToDelete) return;

    const guest = guestToDelete;
    setDeleteDialogOpen(false);
    setGuestToDelete(null);

    try {
      await deleteDoc(doc(db, 'guests', guest.id));
      setSnackbar({
        open: true,
        message: 'Guest deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error("Error deleting guest:", error);
      setSnackbar({
        open: true,
        message: 'Failed to delete guest. Please try again.',
        severity: 'error'
      });
    }
  };

  const dayIsNotInThePast = (dayIndex) => {
    const today = new Date();

    //today at midnight:
    today.setHours(0, 0, 0, 0);

    const dayToCheck = new Date(selectedWeekString);
    dayToCheck.setDate(dayToCheck.getDate() + dayIndex + 1);
    return dayToCheck >= today;
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setGuestToDelete(null);
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      pt: "25px",
      pb: "50px",
      minHeight: '100vh', // Changed from height to minHeight
      bgcolor: '#f5f5f5',
      position: 'relative', // Added position relative
      zIndex: 0 // Added a base z-index
    }}
    ref={scrollContainerRef}
    >
       {refreshing && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      <Card sx={{ maxWidth: 400, width: '100%', m: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" component="h2">
                Weekly Bookings
              </Typography>
              <IconButton 
                onClick={handleManualRefresh} 
                disabled={refreshing}
                aria-label="refresh"
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          <FormControl fullWidth margin="normal">
            <InputLabel>Select Week</InputLabel>
            <Select
              value={selectedWeekString}
              label="Select Week"
              onChange={handleWeekChange}
            >
              {weekOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                   {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
            <Tabs 
              value={selectedDay} 
              onChange={handleDayChange} 
              aria-label="weekday tabs" 
              variant="fullWidth"
              sx={{
                '& .MuiTabs-flexContainer': {
                  justifyContent: 'space-between',
                },
                '& .MuiTab-root': {
                  minWidth: 'auto',
                  padding: '6px 2px',
                  fontSize: '0.8rem',
                },
              }}
            >
              {days.map((day, index) => (
                
                <Tab label={day} key={index} sx={dayIsNotInThePast(index)? {fontWeight: "500"}: {}}  disabled={!dayIsNotInThePast(index)}/>
              ))}
            </Tabs>
          </Box>

          <ToggleButtonGroup
            color="primary"
            value={timeOfDay}
            exclusive
            onChange={handleTimeChange}
            aria-label="Time of day"
            fullWidth
            sx={{ my: 2 }}
          >
            <ToggleButton value="morning" aria-label="morning">
              Morning
            </ToggleButton>
            <ToggleButton value="afternoon" aria-label="afternoon">
              Afternoon
            </ToggleButton>
          </ToggleButtonGroup>
          {dayIsNotInThePast(selectedDay) && !loading && 
            <Box sx={{ width: '100%' }}>
            {getFreeSpots() < 0 ? (
              <Alert severity="error" sx={{ width: '100%' }} variant="outlined" color="error">
                {`Capacity exceeded. Free up ${getFreeSpots()*-1} seat(s).`}
              </Alert>
            ) : (
              <Button
                variant="contained"
                fullWidth
                onClick={() => handleOpenBookGuest({ day: selectedDay, timeOfDay })}
                disabled={getFreeSpots() <= 0 || checkingQueue || hasReleasedAllChildSeats()}
                startIcon={checkingQueue ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ 
                  my: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  padding: "11px"
                }}
              >
                {getFreeSpots() <= 0
                  ? 'No seats available'
                  : hasReleasedAllChildSeats()
                    ? 'Guest seats unavailable'
                    : `Reserve a guest seat (${getFreeSpots()} available)`}
              </Button>
            )}
        </Box>
        }
          <ListItem disablePadding sx={{mt: 2, mb: 2}}>
          <>
                <ListItemText primary={""}
                 secondary={
                  <span
                    onClick={handleWaitlistClick}
                    style={{
                      cursor: 'pointer',
                      textDecoration: 'underline'
                    }}
                  >
                    {`Waitlist: ${waitlistCount}`}
                  </span>
                }
                 secondaryTypographyProps={{
                   color: 'text.secondary',
                   sx: { mt: 0.5 }
                 }}
                  />
                  {maxPlaces > 1 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        aria-label="leave waitlist"
                        onClick={handleLeaveWaitlist}
                        disabled={waitlistDisabled || placesHeld === 0}
                      >
                        {waitlistAction === 'leave'
                          ? <CircularProgress size={20} color="inherit" />
                          : <RemoveIcon fontSize="small" />}
                      </IconButton>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {`${placesHeld} of ${maxPlaces} places`}
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label="join waitlist"
                        onClick={handleJoinWaitlist}
                        disabled={waitlistDisabled || placesHeld >= maxPlaces}
                      >
                        {waitlistAction === 'join'
                          ? <CircularProgress size={20} color="inherit" />
                          : <AddIcon fontSize="small" />}
                      </IconButton>
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={
                        waitlistAction
                          ? <CircularProgress size={16} color="inherit" />
                          : (placesHeld > 0 ? <RemoveIcon /> : <AddIcon />)
                      }
                      onClick={placesHeld > 0 ? handleLeaveWaitlist : handleJoinWaitlist}
                      disabled={waitlistDisabled}
                      sx={{
                        whiteSpace: 'nowrap',
                        minWidth: 'auto',
                        padding: '4px 8px',
                        fontSize: '0.75rem',
                        textTransform: 'none',
                      }}
                    >{placesHeld > 0 ? 'Leave waitlist' : 'Join waitlist'}</Button>
                  )}
                </>
          </ListItem>
           

            {getGuestsForSelectedDayAndTime().length > 0 && (
            <>
             <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom sx={{ 
                paddingBottom: 1
              }}>
                Guests
              </Typography>
              <List>
              {getGuestsForSelectedDayAndTime().map((guest, index) => (
                   <ListItem key={index} disablePadding sx={{ 
                    paddingBottom: 1,  // Adds 8px of padding to the bottom (1 * theme.spacing)
                    '&:last-child': {  // Removes padding from the last item
                      paddingBottom: 0
                    }
                  }}>
                    <ListItemText primary={guest.name} secondary={guest.bookingNote} secondaryTypographyProps={{ color: 'text.secondary' }} />
                    {currentUser && currentUser.uid === guest.userId && (
                      <ListItemSecondaryAction>
                        <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteClick(guest)}>
                          <DeleteIcon /> 
                        </IconButton>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                ))}
              </List>
            </>
          )}
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom sx={{ 
                paddingBottom: 1
              }}>
              Regulars
            </Typography>
          <List >
            
            {getBookingsForSelectedDayAndTime().map((person, index) => (
              <ListItem key={index} disablePadding sx={{ 
                paddingBottom: 1,  // Adds 8px of padding to the bottom (1 * theme.spacing)
                '&:last-child': {  // Removes padding from the last item
                  paddingBottom: 0
                }
              }}>
                <ListItemText primary={person.name} />
               {adminPhoneNumbers.indexOf(currentUser?.phoneNumber) >= 0 && currentUser?.uid !== person.userId && <ListItemSecondaryAction>
                    
                    {dayIsNotInThePast(selectedDay) && 
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleFreeUpSlotClick(selectedDay, timeOfDay, person)}
                        sx={{ 
                          whiteSpace: 'nowrap',
                          minWidth: 'auto',
                          padding: '4px 8px',
                          fontSize: '0.75rem'
                        }}
                      >Release seat</Button>
                    }
                  </ListItemSecondaryAction>
                }
                {currentUser && (currentUser.uid === person.userId || person.additionalUserId === currentUser.uid) && (
                  <ListItemSecondaryAction>
                    
                    {dayIsNotInThePast(selectedDay) && 
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleFreeUpSlotClick(selectedDay, timeOfDay, person)}
                        sx={{ 
                          whiteSpace: 'nowrap',
                          minWidth: 'auto',
                          padding: '4px 8px',
                          fontSize: '0.75rem'
                        }}
                      >Release seat</Button>
                    }
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            ))}
            
          </List>

          {getNotAttendingForSelectedDayAndTime().length > 0 && <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom sx={{ 
              paddingBottom: 1
            }}>
            Not Attending
          </Typography>
          <List>
            {getNotAttendingForSelectedDayAndTime().map((person, index) => (
              <ListItem key={index} disablePadding sx={{ 
                paddingBottom: 1,
                '&:last-child': {
                  paddingBottom: 0
                }
              }}>
                <ListItemText primary={person.name} />
                {currentUser && (currentUser.uid === person.userId || person.additionalUserId === currentUser.uid) &&
                  dayIsNotInThePast(selectedDay) && getFreeSpots() > 0 && (
                  <ListItemSecondaryAction>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={reclaimingId === person.id
                        ? <CircularProgress size={16} color="inherit" />
                        : <AddIcon />}
                      onClick={() => handleReclaimSeatClick(person)}
                      disabled={reclaimingId !== null}
                      sx={{
                        whiteSpace: 'nowrap',
                        minWidth: 'auto',
                        padding: '4px 8px',
                        fontSize: '0.75rem'
                      }}
                    >Reclaim seat</Button>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            ))}
          </List>
          </>}

          <Divider sx={{ my: 2 }} />
          Total: {getBookingsForSelectedDayAndTime().length + getGuestsForSelectedDayAndTime().length}
          
          
         
        </CardContent>
      </Card>
      
      {isBookGuestOpen && 
      <BookGuest
        open={isBookGuestOpen}
        onClose={handleCloseBookGuest}
        weekStart={selectedWeekString}
        schedule={selectedSchedule}
        onBookingMade={onBookingMade}
      />
  }

<Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} >
          {snackbar.message}
        </Alert>
      </Snackbar>

<Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm deletion"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to remove this guest?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} autoFocus variant="contained" color="primary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

    

      <Dialog
        open={freeUpModalOpen}
        onClose={handleFreeUpCancel}
        aria-labelledby="free-up-dialog-title"
        aria-describedby="free-up-dialog-description"
      >
        <DialogTitle id="free-up-dialog-title">{`Release ${spotToFreeUp?.person?.name}'s seat`}</DialogTitle>
        <DialogContent>
          <DialogContentText id="free-up-dialog-description">
            Do you want to free up the <strong>{spotToFreeUp?.timeOfDay} seat</strong> on <strong>{spotToFreeUp && formatDate(selectedWeekString, spotToFreeUp.day)}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFreeUpCancel}>Cancel</Button>
          <Button onClick={handleFreeUpConfirm} autoFocus variant="contained" color="primary">
            Yes, Free Up
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(queueWarning)}
        onClose={handleQueueWarningNo}
        aria-describedby="queue-warning-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="queue-warning-dialog-description">
            <strong>{queueWarning?.name}</strong> is next in the queue. Are you sure you want to take this seat?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleQueueWarningNo}>No</Button>
          <Button onClick={handleQueueWarningYes} autoFocus variant="contained" color="primary">
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <WaitlistModal
        isOpen={isWaitlistModalOpen}
        onClose={() => setIsWaitlistModalOpen(false)}
        weekStart={selectedWeekString}
        day={selectedDay}
        timeOfDay={timeOfDay}
        formatDate={formatDateShort}
      />
    </Box>
  );
};

export default WeeklyBookings;